import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismIcon, PrismModal } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import type { FileWithValidation } from 'lumen/components/general/file-drop-zone';
import GeneralFileDropZone from 'lumen/components/general/file-drop-zone';
import type { FileType } from 'lumen/utils/constants/file-types';

type Step = 'drop-zone' | 'file-ready';

interface SettingsCentralMappingModalsModalUploadMappingSignature {
  Args: {
    isUploadRunning?: boolean;
    onClose: () => void;
    onUpload: (file: File) => void;
  };
}

export default class SettingsCentralMappingModalsModalUploadMapping extends Component<SettingsCentralMappingModalsModalUploadMappingSignature> {
  @tracked file: File | null = null;
  @tracked isTooManyFiles = false;
  @tracked isValid = false;
  @tracked step: Step = 'drop-zone';

  allowedFormats: FileType[] = ['xlsx'];

  get isUploadDisabled(): boolean {
    return !this.isValid || !this.file || Boolean(this.args.isUploadRunning);
  }

  @action
  closeModal(): void {
    if (this.args.isUploadRunning) {
      return;
    }

    this.args.onClose();
  }

  @action
  goBack(): void {
    this.step = 'drop-zone';
    this.file = null;
    this.isValid = false;
    this.isTooManyFiles = false;
  }

  @action
  handleUpload(): void {
    if (!this.file || !this.isValid) {
      return;
    }

    this.args.onUpload(this.file);
  }

  @action
  onFileDrop(
    filesWithValidation: FileWithValidation[],
    isTooManyFiles: boolean,
  ): void {
    const [droppedFile] = filesWithValidation;

    if (droppedFile) {
      this.file = droppedFile.file;
      this.isValid = droppedFile.validationErrors.length === 0;
      this.isTooManyFiles = isTooManyFiles;

      this.step = 'file-ready';
    }
  }

  <template>
    <PrismModal
      @isCloseDisabled={{@isUploadRunning}}
      @onClose={{this.closeModal}}
      @title={{t "settings.central_mapping.upload_mapping"}}
      data-test-id="upload-mapping-modal"
    >
      <:default>
        <div class="modal--padded modal--small flex flex-col gap-500">
          {{#if (eq this.step "drop-zone")}}
            <GeneralFileDropZone
              @allowedFormats={{this.allowedFormats}}
              @onFileDrop={{this.onFileDrop}}
              class="min-h-[240px]"
            />
          {{/if}}

          {{#if (eq this.step "file-ready")}}
            <div class="bg-neutral-subdued flex gap-200 px-400 py-500">
              <PrismIcon @name="file" />
              <div
                class="heading-4 mr-auto truncate"
                data-test-id="upload-mapping-file-name"
                title={{if this.file this.file.name ""}}
              >
                {{#if this.file}}
                  {{this.file.name}}
                {{/if}}
              </div>
              {{#if this.isValid}}
                <PrismIcon
                  @color="success"
                  @name="circle-checkmark"
                  data-test-id="upload-mapping-file-valid"
                />
              {{else}}
                <PrismIcon
                  @color="critical"
                  @name="circle-cross"
                  data-test-id="upload-mapping-file-invalid"
                />
              {{/if}}
            </div>
          {{/if}}
        </div>
      </:default>
      <:footer>
        <div class="mx-500 flex h-full items-center justify-end gap-200">
          {{#if (eq this.step "file-ready")}}
            <PrismButton
              @buttonType="ghost"
              @iconBefore="arrow-left"
              @isDisabled={{@isUploadRunning}}
              @label={{t "general.back"}}
              @onClick={{this.goBack}}
              class="mr-auto"
              data-test-id="upload-mapping-back"
            />
          {{/if}}
          <PrismButton
            @buttonType="secondary"
            @isDisabled={{@isUploadRunning}}
            @label={{t "general.cancel"}}
            @onClick={{this.closeModal}}
            data-test-id="upload-mapping-cancel"
          />
          <PrismButton
            @isDisabled={{this.isUploadDisabled}}
            @isLoading={{@isUploadRunning}}
            @label={{t "general.upload"}}
            @onClick={{this.handleUpload}}
            data-test-id="upload-mapping-upload"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
